import {
  OUTPUT_ITEM_CALCULATED_FIELDS as BUILD_OUTPUT_ITEM_CALCULATED_FIELDS,
  INPUT_ITEM_CALCULATED_FIELDS as BUILD_INPUT_ITEM_CALCULATED_FIELDS,
} from "services/sosInventoryService/build/schema";
import {
  getItemRecord,
  getMultipleItemRecords,
} from "services/sosInventoryService/domainLogic";
import {
  OUTPUT_ITEM_CALCULATED_FIELDS as PROCESS_OUTPUT_ITEM_CALCULATED_FIELDS,
  INPUT_ITEM_CALCULATED_FIELDS as PROCESS_INPUT_ITEM_CALCULATED_FIELDS,
} from "services/sosInventoryService/process/schema";
import { descriptionForPurchasingTransactions } from "services/sosInventoryService/purchasingTransaction/domainLogic";
import { getItemBom } from "services/sosInventoryService/sosApi";
import { setOutboundBinsForLine } from "services/utility/bins";
import { getUomConversionFromUomReference } from "services/utility/uoms";
import { getBaseUom } from "services/utility/uoms";

import globalState from "globalState/globalState";

import { ITEM_TYPES } from "appConstants";
import { EMPTY_INPUT_LINE_ITEM, EMPTY_OUTPUT_LINE_ITEM } from "editConfig";

export async function updateLineWithItem(
  item,
  line,
  record,
  lineType = "input"
) {
  const { location } = record;
  if (!item) {
    return line;
  }
  // transfer the appropriate properties from the new inventory
  // item to the line item
  let newLine = {
    ...line,
    item,
    lot: null,
    description: descriptionForPurchasingTransactions(item),
    uom: getBaseUom(item.uoms),
    class: item.class,
    serials: [],
    available: item.available,
    onhand: item.onhand,
    onSO: item.onSO,
    relatedRecords: { item },
    itemDetails: {
      itemUoms: item.uoms,
      serialTracking: item.serialTracking,
      lotTracking: item.lotTracking,
      type: item.type,
    },
  };

  // set the default bin and the available bins for the dropdown
  if (lineType === "input") {
    newLine = await setOutboundBinsForLine(location, newLine);
  } else if (lineType === "output") {
    newLine.bin = item.bin;
  }

  return newLine;
}

export function updateWorkOrderLineWithItem(item, line) {
  const newLine = {
    ...line,
    item,
    description: item.description,
    class: item.class,
    uom: getBaseUom(item.uoms),
    weightunit: item.weightUnit,
    volumeunit: item.volumeUnit,
    relatedRecords: { item },
    itemDetails: {
      itemWeight: item.weight,
      itemVolume: item.volume,
      itemUoms: item.uoms,
      type: item.type,
    },
  };

  return newLine;
}

export async function updateBuildLineItems(
  date,
  location,
  lines,
  lineType = "input"
) {
  const { showQuantityOnBuildsAndShipments } =
    globalState.getState().userCompanySettings.settings;

  // used to update the fields reliant on location and date
  const newLines = await Promise.all(
    lines.map(async (line) => {
      if (line.item?.id && location) {
        const isOutputLine = lineType === "output";
        const baseItemFields = isOutputLine
          ? BUILD_OUTPUT_ITEM_CALCULATED_FIELDS
          : BUILD_INPUT_ITEM_CALCULATED_FIELDS;
        const itemFields = showQuantityOnBuildsAndShipments
          ? ["onhand", ...baseItemFields]
          : baseItemFields;

        const item = await getItemRecord(
          line.item.id,
          location?.id,
          date,
          itemFields
        );
        const uom = item.uoms.find(({ uom }) => uom.id === line.uom?.id);
        let newLine = {
          ...line,
          relatedRecords: { item },
          onhand: item.onhand,
          uom: uom ? { ...uom, id: line.uom.id } : null,
        };
        if (isOutputLine) {
          newLine.onSO = item.onSO;
        } else {
          newLine = await setOutboundBinsForLine(location, newLine);
        }
        return newLine;
      } else {
        const newLine = {
          ...line,
          availableBins: [],
          bin: null,
        };
        return newLine;
      }
    })
  );
  return newLines;
}

export async function updateProcessInputs(date, location, lines) {
  const { showQuantityOnBuildsAndShipments } =
    globalState.getState().userCompanySettings.settings;

  return await Promise.all(
    lines.map(async (line) => {
      let newLine = { ...line };
      if (line.item?.id && location) {
        const itemFields = showQuantityOnBuildsAndShipments
          ? ["onhand", ...PROCESS_INPUT_ITEM_CALCULATED_FIELDS]
          : PROCESS_INPUT_ITEM_CALCULATED_FIELDS;

        const item = await getItemRecord(
          line.item.id,
          location?.id,
          date,
          itemFields
        );
        const uom = item.uoms.find(({ uom }) => uom.id === line.uom?.id);

        newLine.relatedRecords = { ...line.relatedRecords, item };
        newLine.onhand = item.onhand;
        newLine.uom = uom ? { ...uom, id: line.uom.id } : null;
        newLine = await setOutboundBinsForLine(location, newLine);
      } else {
        newLine.availableBins = [];
        newLine.bin = null;
      }
      return newLine;
    })
  );
}

export async function updateInputs(outputs, date, location) {
  const { showQuantityOnBuildsAndShipments } =
    globalState.getState().userCompanySettings.settings;

  const output = outputs[0];
  if (output?.itemDetails?.type !== ITEM_TYPES.ASSEMBLY) {
    return null;
  }

  const bom = await getItemBom(output.item.id);
  const bomIds = bom.map(({ componentItem: { id } }) => id);
  const items = await getMultipleItemRecords(
    bomIds,
    location?.id,
    date,
    showQuantityOnBuildsAndShipments
      ? ["onhand", ...BUILD_INPUT_ITEM_CALCULATED_FIELDS]
      : BUILD_INPUT_ITEM_CALCULATED_FIELDS
  );

  const newInputs = await Promise.all(
    bom.map(async ({ componentItem, quantity, notes }) => {
      const item = items.find(({ id }) => id === componentItem.id);

      if (item) {
        const outputConversion = getUomConversionFromUomReference(
          output.uom,
          output.itemDetails.itemUoms
        );
        const usedPer = quantity.times(outputConversion);
        let newLine = {
          ...EMPTY_INPUT_LINE_ITEM.build,
          item: componentItem,
          description: descriptionForPurchasingTransactions(item),
          class: item.class,
          weightunit: item.weightUnit,
          volumeunit: item.volumeUnit,
          uom: getBaseUom(item.uoms),
          onhand: item.onhand,
          usedPer,
          bin: location.binTracking ? item.bin ?? null : null,
          quantity: usedPer.times(output.quantity),
          notes,
          relatedRecords: { item },
          itemDetails: {
            itemWeight: item.weight,
            itemVolume: item.volume,
            itemUoms: item.uoms,
            serialTracking: item.serialTracking,
            lotTracking: item.lotTracking,
            type: item.type,
          },
        };
        newLine = await setOutboundBinsForLine(location, newLine);
        return newLine;
      }
      return null;
    })
  );

  return newInputs.filter((input) => input);
}

export function updateByMultiplier(multiplier, outputs, inputs) {
  const newOutputs = [...outputs];
  const newInputs = [...inputs];
  outputs.forEach((line, i) => {
    newOutputs[i].quantity = line.quantity.times(multiplier);
  });
  inputs.forEach((line, i) => {
    newInputs[i].quantity = line.quantity.times(multiplier);
  });
  return { newOutputs, newInputs };
}

export async function addTemplateLines(record) {
  const { template, location, date } = record;
  const { showQuantityOnBuildsAndShipments } =
    globalState.getState().userCompanySettings.settings;

  const outputItemPromises = [];
  for (let i = 0; i < template.outputs.length; i++) {
    const output = template.outputs[i];
    if (output.item?.id) {
      outputItemPromises.push(
        getItemRecord(
          output.item.id,
          location?.id,
          date,
          PROCESS_OUTPUT_ITEM_CALCULATED_FIELDS
        )
      );
    }
  }

  const inputItemPromises = [];
  for (let i = 0; i < template.inputs.length; i++) {
    const input = template.inputs[i];
    if (input.item?.id) {
      inputItemPromises.push(
        getItemRecord(
          input.item.id,
          location?.id,
          date,
          showQuantityOnBuildsAndShipments
            ? ["onhand", ...PROCESS_INPUT_ITEM_CALCULATED_FIELDS]
            : PROCESS_INPUT_ITEM_CALCULATED_FIELDS
        )
      );
    }
  }

  const outputInventoryItems = await Promise.all(outputItemPromises);
  const inputInventoryItems = await Promise.all(inputItemPromises);
  const newOutputs = await Promise.all(
    template.outputs.map(async ({ cost, quantity, waste, item }) => {
      const fullItemData = outputInventoryItems.find(
        ({ id }) => id === item?.id
      );
      const { serialTracking, uoms, type } = fullItemData;
      const line = {
        ...EMPTY_OUTPUT_LINE_ITEM.process,
        item,
        cost,
        quantity,
        waste,
        description: descriptionForPurchasingTransactions(fullItemData),
        uom: getBaseUom(uoms),
        class: fullItemData.class,
        itemDetails: { type, itemUoms: uoms, serialTracking },
        relatedRecords: { item: fullItemData },
      };

      const newLine = updateLineWithItem(fullItemData, line, record, "output");
      return newLine;
    })
  );

  const newInputs = await Promise.all(
    template.inputs.map(async ({ cost, quantity, item }) => {
      const fullItemData = inputInventoryItems.find(
        ({ id }) => id === item?.id
      );
      const { serialTracking, uoms, type, onhand, lotTracking } = fullItemData;
      const line = {
        ...EMPTY_INPUT_LINE_ITEM.process,
        item,
        cost,
        quantity,
        description: descriptionForPurchasingTransactions(fullItemData),
        onhand,
        uom: getBaseUom(uoms),
        class: fullItemData.class,
        itemDetails: { type, itemUoms: uoms, serialTracking, lotTracking },
        relatedRecords: { item: fullItemData },
      };

      const newLine = updateLineWithItem(fullItemData, line, record, "input");
      return newLine;
    })
  );
  return { newOutputs, newInputs };
}
